import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import useSeo from '../hooks/useSeo';
import { Accordion, BrowserFrame, CheckList, PageHero, PageShell, SectionIntro } from './home/blocks';
import { shotUrl } from './home/shots';
import { BrandButton, Kicker } from './home/ui';
import CtaBanner from './home/CtaBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const paragraphs = (text) =>
  String(text || '')
    .split(/\n{2,}|\n/)
    .map((p) => p.trim())
    .filter(Boolean);

const SeoLandingPage = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const lang = (i18n.language || 'nl').slice(0, 2);
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    setStatus('loading');
    axios
      .get(`${BACKEND_URL}/api/landing-pages/${slug}`)
      .then((res) => {
        setPage(res.data);
        setStatus('ok');
      })
      .catch(() => setStatus('notfound'));
  }, [slug]);

  const jsonLd = page
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            name: page.title,
            description: page.meta_description,
            url: typeof window !== 'undefined' ? window.location.href : '',
            isPartOf: { '@type': 'WebSite', name: 'WowProfit.io', url: 'https://wowprofit.io' },
          },
          ...(page.faq?.length
            ? [
                {
                  '@type': 'FAQPage',
                  mainEntity: page.faq.map((f) => ({
                    '@type': 'Question',
                    name: f.question,
                    acceptedAnswer: { '@type': 'Answer', text: f.answer },
                  })),
                },
              ]
            : []),
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wowprofit.io/' },
              { '@type': 'ListItem', position: 2, name: page.h1 },
            ],
          },
        ],
      }
    : null;

  useSeo({
    path: page ? `/${page.slug}` : undefined,
    title: page?.title,
    description: page?.meta_description,
    noindex: page?.noindex,
    jsonLd,
    ogType: 'article',
  });

  if (status === 'loading') {
    return (
      <PageShell>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand" />
        </div>
      </PageShell>
    );
  }

  if (status === 'notfound') {
    return (
      <PageShell testId="landing-page-notfound">
        <PageHero hero={{ kicker: '404', title: `*${t('landing_page_ui.not_found')}*`, subtitle: t('landing_page_ui.not_found_text') }}>
          <div className="mt-[clamp(24px,1.9444vw,35px)] flex flex-col items-center justify-center gap-3 pb-[clamp(56px,6.6667vw,120px)] min-[420px]:flex-row min-[420px]:gap-5">
            <BrandButton to="/">{t('landing_page_ui.to_home')}</BrandButton>
            <BrandButton to="/contact" variant="outline">Contact</BrandButton>
          </div>
        </PageHero>
      </PageShell>
    );
  }

  const intro = paragraphs(page.intro);
  const screen = shotUrl('overzicht', lang);

  return (
    <PageShell testId="seo-landing-page">
      <PageHero hero={{ kicker: page.keyword, title: page.h1, subtitle: intro[0] }}>
        {intro.slice(1).map((p, i) => (
          <p key={i} className="mx-auto mt-4 max-w-[33.55em] text-[clamp(16px,1.2222vw,22px)] leading-[1.7] text-[#273845]">
            {p}
          </p>
        ))}
        <div className="mt-[clamp(24px,1.9444vw,35px)] flex flex-col items-center justify-center gap-3 min-[420px]:flex-row min-[420px]:gap-5">
          <BrandButton to="register" data-testid="landing-hero-cta">{page.cta_button || t('landing_page_ui.cta_button')}</BrandButton>
          <BrandButton to="/hoe-het-werkt" variant="outline">{t('landing_page_ui.see_how')}</BrandButton>
        </div>
        <BrowserFrame src={screen} alt={page.h1} fit="cover" className="mx-auto mt-[clamp(40px,3.8333vw,69px)] max-w-[1141px] text-left" />
      </PageHero>

      {page.key_takeaways?.length > 0 && (
        <section className="hp-container mt-[clamp(48px,5.3333vw,96px)]">
          <div className="hp-card hp-card-navy mx-auto max-w-[1200px] bg-brand-mint px-5 py-8 sm:px-10 sm:py-12 lg:px-[clamp(48px,5.5556vw,100px)] lg:py-[clamp(40px,4.4444vw,80px)]" data-testid="landing-takeaways">
            <Kicker className="text-brand-ink">{t('landing_page_ui.takeaways_title')}</Kicker>
            <CheckList items={page.key_takeaways} />
          </div>
        </section>
      )}

      {page.blocks?.length > 0 && (
        <section className="hp-container mt-[clamp(32px,3.3333vw,60px)]">
          <div className="hp-card hp-card-navy mx-auto max-w-[1200px] bg-white px-5 py-8 sm:px-10 sm:py-12 lg:px-[clamp(48px,5.5556vw,100px)] lg:py-[clamp(48px,5vw,90px)]">
            {page.blocks.map((block, i) => (
              <div
                key={i}
                data-testid={`landing-block-${i}`}
                className={i > 0 ? 'mt-[clamp(32px,3.3333vw,60px)] border-t border-brand-line pt-[clamp(32px,3.3333vw,60px)]' : ''}
              >
                <h2 className="mb-5 text-[clamp(24px,2vw,36px)] font-semibold leading-tight text-[#273845]">{block.heading}</h2>
                {paragraphs(block.body).map((p, j) => (
                  <p
                    key={j}
                    className="faq-answer mb-4 text-[clamp(16px,1vw,18px)] leading-[1.75] text-[#273845] last:mb-0"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p) }}
                  />
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {page.faq?.length > 0 && (
        <>
          <SectionIntro title={`*${t('landing_page_ui.faq_title')}*`} />
          <section className="hp-container mt-[clamp(28px,4vw,72px)]" data-testid="landing-faq">
            <div className="mx-auto max-w-[1200px]">
              <Accordion items={page.faq} renderAnswer={(item) => item.answer} />
            </div>
          </section>
        </>
      )}

      <div className="pt-[clamp(0px,5.3333vw,96px)]">
        <CtaBanner
          wide
          cta={{
            title: page.cta_title || t('landing_page_ui.cta_title'),
            subtitle: page.cta_text || t('landing_page_ui.cta_text'),
            primary_label: page.cta_button || t('landing_page_ui.cta_button'),
            secondary_label: t('landing_page_ui.see_how'),
            secondary_link: '/hoe-het-werkt',
          }}
        />
      </div>
    </PageShell>
  );
};

export default SeoLandingPage;
